import React, { useEffect, useMemo, useState } from 'react';
import { money } from '../../lib/theme';
import { ConfidencePill } from '../ConfidencePill';
import BucketPill from '../BucketPill';
import { getBuckets } from '../../lib/api';
import { groupByBucket } from '../../lib/categoryGrouping';

const MONEY_ROWS = [
  { key: 'subtotal', label: 'Subtotal', kind: 'decimal' },
  { key: 'gst', label: 'GST (TPS)', kind: 'decimal' },
  { key: 'qst', label: 'QST (TVQ)', kind: 'decimal' },
  { key: 'total', label: 'Total', kind: 'decimal' },
];

/**
 * Presents the vendor/date/money/category/notes editable fields for a receipt.
 * All edits are surfaced to the parent via `openEdit(field)` and `applyPatch(patch)`.
 */
export default function ReceiptForm({
  data,
  categories,
  notes, setNotes, setNotesDirty, onSaveNotes,
  openEdit,
  applyPatch,
  colors,
  suggestionSlot,
}) {
  // Filter Uncategorized sentinel out of picker (Phase E: memoize)
  const pickableCategories = useMemo(
    () => categories.filter((c) => !c.is_special),
    [categories],
  );

  // Phase 21 — load bucket catalog for pills.
  const [buckets, setBuckets] = useState([]);
  useEffect(() => {
    getBuckets().then(setBuckets).catch(() => setBuckets([]));
  }, []);
  const currentCat = useMemo(
    () => categories.find((c) => c.name === data.category),
    [categories, data.category],
  );

  return (
    <>
      {/* Vendor card */}
      <div
        className="rounded-xl border p-5 mb-4"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <button
            data-testid="detail-vendor"
            onClick={() => openEdit({ key: 'vendor', label: 'Vendor', kind: 'text' })}
            className="text-2xl font-bold tracking-tight text-left flex-1 transition-opacity hover:opacity-70"
            style={{ color: colors.text }}
          >
            {data.vendor?.value ?? 'Unknown vendor'}
          </button>
          <ConfidencePill confidence={data.vendor?.confidence ?? 0} />
        </div>
        <div className="flex items-center justify-between">
          <button
            data-testid="detail-date"
            onClick={() => openEdit({ key: 'date', label: 'Date', kind: 'date' })}
            className="text-sm tabular-nums transition-opacity hover:opacity-70"
            style={{ color: colors.textMuted }}
          >
            {data.date?.value ?? 'Unknown date'}
          </button>
          <ConfidencePill confidence={data.date?.confidence ?? 0} />
        </div>
      </div>

      {/* Category */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <p
            className="text-[11px] font-semibold uppercase tracking-wider"
            style={{ color: colors.textMuted }}
          >
            Category
          </p>
          {currentCat?.bucket ? (
            <BucketPill bucket={currentCat.bucket} catalog={buckets} />
          ) : null}
        </div>
        {suggestionSlot}
        <div className="flex flex-col gap-3" data-testid="category-picker">
          {groupByBucket(pickableCategories, buckets).map((row) => (
            <div key={row.bucket}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: row.color }} />
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                  {row.label}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {row.categories.map((cat) => (
                  <button
                    key={cat.id}
                    data-testid={`category-${cat.name}`}
                    onClick={() => applyPatch({ category: data.category === cat.name ? null : cat.name })}
                    className="px-3 py-1.5 rounded-full border text-[13px] font-medium transition-colors inline-flex items-center gap-1.5"
                    style={{
                      backgroundColor: data.category === cat.name ? colors.accentSoft : colors.surface,
                      borderColor: data.category === cat.name ? colors.accent : colors.border,
                      color: data.category === cat.name ? colors.accent : colors.text,
                      fontWeight: data.category === cat.name ? 700 : 500,
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Money card */}
      <div
        className="rounded-xl border overflow-hidden mb-4"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        data-testid="money-card"
      >
        {MONEY_ROWS.map((row, idx) => {
          const f = data[row.key];
          const isDerived =
            (row.key === 'gst' || row.key === 'qst') &&
            data.combined_tax?.value !== null &&
            data.combined_tax?.value !== undefined &&
            f?.confidence > 0 && f?.confidence <= 0.75;
          const showCombinedAfter =
            row.key === 'qst' &&
            data.combined_tax?.value !== null &&
            data.combined_tax?.value !== undefined &&
            data.combined_tax?.value > 0;

          return (
            <React.Fragment key={row.key}>
              <button
                data-testid={`detail-row-${row.key}`}
                onClick={() => openEdit(row)}
                className="w-full flex items-center justify-between px-5 py-3.5 transition-opacity hover:opacity-80"
                style={{
                  borderBottom: (idx < MONEY_ROWS.length - 1 || showCombinedAfter) ? `1px solid ${colors.border}` : 'none',
                  backgroundColor: row.key === 'total' ? colors.accentSoft : 'transparent',
                }}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-sm"
                    style={{
                      color: row.key === 'total' ? colors.text : colors.textMuted,
                      fontWeight: row.key === 'total' ? 600 : 400,
                    }}
                  >
                    {row.label}
                  </span>
                  {isDerived && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: colors.accentSoft, color: colors.accent }}
                    >
                      Derived
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`tabular-nums ${row.key === 'total' ? 'text-xl font-bold' : 'text-sm font-semibold'}`}
                    style={{ color: colors.text }}
                  >
                    {money(f?.value)}
                  </span>
                  <ConfidencePill confidence={f?.confidence ?? 0} />
                </div>
              </button>

              {showCombinedAfter && (
                <div
                  className="flex items-center justify-between px-5 py-3"
                  style={{
                    backgroundColor: colors.bg,
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  <span className="text-xs italic" style={{ color: colors.textMuted }}>
                    Combined tax (derived split)
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold tabular-nums" style={{ color: colors.text }}>
                      {money(data.combined_tax.value)}
                    </span>
                    <ConfidencePill confidence={data.combined_tax.confidence} />
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Notes */}
      <div
        className="rounded-xl border p-5 mb-4"
        style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      >
        <label
          className="text-[11px] font-semibold uppercase tracking-wider block mb-2"
          style={{ color: colors.textMuted }}
          htmlFor="detail-notes"
        >
          Notes
        </label>
        <textarea
          id="detail-notes"
          data-testid="detail-notes"
          value={notes}
          onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
          onBlur={onSaveNotes}
          placeholder="Optional context (e.g. client, project, tag)"
          className="w-full min-h-[80px] bg-transparent outline-none text-sm resize-none placeholder:opacity-50"
          style={{ color: colors.text }}
        />
      </div>
    </>
  );
}
